import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type {
  AppData, BodyWeightEntry, CoachNote, Exercise, PlannedExercise, ScheduledWorkout, Settings, SyncMutation, SyncState,
  TrainingProgram, WellnessEntry, WorkoutSession,
} from '../types';
import { api } from '../lib/api';
import { cacheData, clearOfflineData, enqueueMutation, pendingMutations, readCachedData, rebaseMutation, removeMutations, updateEntityBaseVersion } from '../lib/offline-db';

type Conflict = { id: string; entityKey: string; serverVersion: number; message: string };

interface ContextValue {
  data?: AppData;
  loading: boolean;
  authenticated: boolean;
  offlineUnlocked: boolean;
  error?: string;
  syncState: SyncState;
  pendingCount: number;
  conflicts: Conflict[];
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  sync: () => Promise<void>;
  keepLocalConflict: (conflict: Conflict) => Promise<void>;
  useServerConflict: (conflict: Conflict) => Promise<void>;
  saveWorkout: (workout: ScheduledWorkout) => Promise<void>;
  removeWorkout: (workout: ScheduledWorkout) => Promise<void>;
  startWorkout: (workout: ScheduledWorkout, wellness?: WellnessEntry) => Promise<WorkoutSession>;
  saveSession: (session: WorkoutSession) => Promise<void>;
  saveExercise: (exercise: Exercise) => Promise<void>;
  saveBodyWeight: (entry: BodyWeightEntry) => Promise<void>;
  removeBodyWeight: (entry: BodyWeightEntry) => Promise<void>;
  saveSettings: (settings: Settings) => Promise<void>;
  dismissCoachNote: (note: CoachNote) => Promise<void>;
  cloneProgram: (program: TrainingProgram, startDate: string) => Promise<void>;
  replaceData: (data: AppData) => Promise<void>;
}

const AppContext = createContext<ContextValue | null>(null);

function mutation(type: SyncMutation['type'], entityKey: string, payload: unknown, baseVersion?: number): SyncMutation {
  return { id: crypto.randomUUID(), type, entityKey, payload, baseVersion, createdAt: new Date().toISOString() };
}

function entityVersion(data: AppData, item: SyncMutation): number | undefined {
  const id = (item.payload as { id?: string } | undefined)?.id;
  if (!id) return undefined;
  if (item.type === 'upsert_session') return data.sessions.find((entry) => entry.id === id)?.version;
  if (item.type === 'upsert_workout') return data.workouts.find((entry) => entry.id === id)?.version;
  if (item.type === 'upsert_exercise') return data.exercises.find((entry) => entry.id === id)?.version;
  if (item.type === 'upsert_body_weight') return data.bodyWeights.find((entry) => entry.id === id)?.version;
  return undefined;
}

function createSets(item: PlannedExercise, _loadType?: Exercise['loadType']) {
  return Array.from({ length: item.plannedSets }, (_, index) => ({
    id: crypto.randomUUID(), setNumber: index + 1, setType: 'working' as const,
    reps: undefined, rpe: undefined, weightKg: undefined, heightCm: undefined, distanceM: undefined, durationSeconds: undefined,
    completed: false, failed: false, toFailure: false,
  }));
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppData>();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [offlineUnlocked, setOfflineUnlocked] = useState(false);
  const [error, setError] = useState<string>();
  const [syncState, setSyncState] = useState<SyncState>('saved');
  const [pendingCount, setPendingCount] = useState(0);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const syncing = useRef(false);
  const dataRef = useRef<AppData | undefined>(undefined);
  const syncTimer = useRef<number | undefined>(undefined);

  const persistLocal = useCallback(async (next: AppData) => {
    dataRef.current = next;
    setData(next);
    await cacheData(next);
  }, []);

  useEffect(() => { dataRef.current = data; }, [data]);

  const sync = useCallback(async () => {
    if (syncing.current) return;
    const queued = await pendingMutations();
    setPendingCount(queued.length);
    if (!queued.length) { setSyncState(navigator.onLine ? 'saved' : 'offline'); return; }
    if (!navigator.onLine) { setSyncState('offline'); return; }
    syncing.current = true;
    setSyncState('saving');
    let syncAgain = false;
    try {
      const result = await api<{
        results: Array<{ id: string; status: string; error?: string; serverVersion?: number }>;
        data: AppData;
      }>('/api/sync', { method: 'POST', body: JSON.stringify({ mutations: queued }) });
      const completed = result.results.filter((item) => item.status === 'applied' || item.status === 'duplicate').map((item) => item.id);
      await removeMutations(completed);
      const conflictRows = result.results.filter((item) => item.status === 'conflict').map((item) => {
        const original = queued.find((queuedItem) => queuedItem.id === item.id)!;
        return { id: item.id, entityKey: original.entityKey, serverVersion: item.serverVersion ?? 1, message: item.error ?? 'Конфликт изменений' };
      });
      const failedRows = result.results.filter((item) => item.status === 'error');
      setConflicts(conflictRows);
      let rest = await pendingMutations();
      const appliedEntities = queued.filter((item) => completed.includes(item.id));
      for (const applied of appliedEntities) {
        if (!rest.some((item) => item.entityKey === applied.entityKey)) continue;
        const version = entityVersion(result.data, applied);
        if (version !== undefined) await updateEntityBaseVersion(applied.entityKey, version);
      }
      if (appliedEntities.length) rest = await pendingMutations();
      if (!conflictRows.length && !rest.length) await persistLocal(result.data);
      setPendingCount(rest.length);
      syncAgain = !conflictRows.length && !failedRows.length && rest.length > 0;
      if (failedRows.length) setError(failedRows[0].error ?? 'Не удалось сохранить изменение');
      else setError(undefined);
      setSyncState(conflictRows.length ? 'conflict' : failedRows.length ? 'error' : rest.length ? 'saving' : 'saved');
    } catch (caught) {
      setSyncState(navigator.onLine ? 'error' : 'offline');
      setError(caught instanceof Error ? caught.message : 'Не удалось синхронизировать');
    } finally {
      syncing.current = false;
      if (syncAgain) window.setTimeout(() => void sync(), 50);
    }
  }, [persistLocal]);

  const refresh = useCallback(async () => {
    try {
      const fresh = await api<AppData>('/api/bootstrap');
      const cached = await readCachedData();
      if (cached && cached.user.id !== fresh.user.id) {
        // The browser cache is device-local. Never send one account's offline queue
        // after another account signs in on the same device.
        await clearOfflineData();
        await persistLocal(fresh);
      } else {
        const queued = await pendingMutations();
        if (!queued.length) await persistLocal(fresh);
        else await persistLocal(cached ? { ...cached, user: fresh.user } : fresh);
      }
      setAuthenticated(true); setOfflineUnlocked(false); setError(undefined);
      await sync();
    } catch (caught) {
      const cached = await readCachedData();
      if (cached) {
        setData(cached); setAuthenticated(true); setOfflineUnlocked(true); setSyncState('offline');
      } else {
        setAuthenticated(false); setError(caught instanceof Error ? caught.message : 'Нет связи с сервером');
      }
    }
  }, [persistLocal, sync]);

  useEffect(() => {
    void (async () => {
      try {
        const status = await api<{ authenticated: boolean }>('/api/auth/status');
        if (status.authenticated) await refresh();
        else {
          const cached = await readCachedData();
          if (!navigator.onLine && cached) { setData(cached); setAuthenticated(true); setOfflineUnlocked(true); setSyncState('offline'); }
        }
      } catch {
        const cached = await readCachedData();
        if (cached) { setData(cached); setAuthenticated(true); setOfflineUnlocked(true); setSyncState('offline'); }
      } finally { setLoading(false); }
    })();
  }, [refresh]);

  useEffect(() => {
    const online = () => { setOfflineUnlocked(false); void refresh(); };
    const offline = () => setSyncState('offline');
    const visibility = () => { if (document.visibilityState === 'hidden') void sync(); else if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('online', online); window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visibility);
    const interval = window.setInterval(() => void sync(), 30_000);
    return () => {
      window.removeEventListener('online', online); window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visibility); window.clearInterval(interval);
    };
  }, [refresh, sync]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (pendingCount > 0) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [pendingCount]);

  const queue = useCallback(async (item: SyncMutation, next: AppData) => {
    await enqueueMutation(item); await persistLocal(next); setPendingCount((count) => Math.max(1, count + 1));
    setSyncState(navigator.onLine ? 'saving' : 'offline');
    if (syncTimer.current !== undefined) window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => { syncTimer.current = undefined; void sync(); }, 650);
  }, [persistLocal, sync]);

  const loginUser = useCallback(async (loginValue: string, password: string) => {
    await api('/api/auth/login', { method: 'POST', body: JSON.stringify({ login: loginValue, password }) });
    setAuthenticated(true); await refresh();
  }, [refresh]);

  const logoutUser = useCallback(async () => {
    if (!offlineUnlocked) {
      try { await api('/api/auth/logout', { method: 'POST' }); } catch { /* сессия уже могла быть завершена */ }
    }
    await clearOfflineData(); setData(undefined); setAuthenticated(false); setOfflineUnlocked(false);
  }, [offlineUnlocked]);

  const saveWorkout = useCallback(async (workout: ScheduledWorkout) => {
    if (!data) return;
    const current = data.workouts.find((item) => item.id === workout.id);
    const nextWorkout = { ...workout, updatedAt: new Date().toISOString() };
    const workouts = current ? data.workouts.map((item) => item.id === workout.id ? nextWorkout : item) : [...data.workouts, nextWorkout];
    await queue(mutation('upsert_workout', `workout:${workout.id}`, nextWorkout, current?.version), { ...data, workouts });
  }, [data, queue]);

  const removeWorkout = useCallback(async (workout: ScheduledWorkout) => {
    if (!data) return;
    await queue(mutation('delete_workout', `workout:${workout.id}`, { id: workout.id }, workout.version), { ...data, workouts: data.workouts.filter((item) => item.id !== workout.id) });
  }, [data, queue]);

  const startWorkout = useCallback(async (workout: ScheduledWorkout, wellness?: WellnessEntry) => {
    if (!data) throw new Error('Данные не загружены');
    const timestamp = new Date().toISOString();
    const session: WorkoutSession = {
      id: crypto.randomUUID(), scheduledWorkoutId: workout.id, date: workout.date, title: workout.title,
      startedAt: timestamp, status: 'in_progress', preWellness: wellness,
      exercises: workout.exercises.filter((item) => item.enabled !== false).map((item, index) => {
        const exercise = data.exercises.find((entry) => entry.id === item.exerciseId)!;
        return { id: crypto.randomUUID(), exerciseId: item.exerciseId, exerciseName: exercise?.name ?? 'Упражнение', orderIndex: index,
          section: item.section, plannedExerciseId: item.id, status: 'planned', restSeconds: item.restSeconds,
          note: item.note, sets: createSets(item, exercise?.loadType) };
      }),
      version: 0, updatedAt: timestamp,
    };
    const workouts = data.workouts.map((item) => item.id === workout.id ? { ...item, status: 'started' as const } : item);
    await queue(mutation('upsert_session', `session:${session.id}`, session, undefined), { ...data, workouts, sessions: [session, ...data.sessions] });
    return session;
  }, [data, queue]);

  const saveSession = useCallback(async (session: WorkoutSession) => {
    const currentData = dataRef.current;
    if (!currentData) return;
    const current = currentData.sessions.find((item) => item.id === session.id);
    const nextSession = { ...session, updatedAt: new Date().toISOString() };
    const sessions = current ? currentData.sessions.map((item) => item.id === session.id ? nextSession : item) : [nextSession, ...currentData.sessions];
    const workouts = nextSession.scheduledWorkoutId ? currentData.workouts.map((item) => item.id === nextSession.scheduledWorkoutId ? {
      ...item,
      status: nextSession.status === 'completed' ? 'completed' as const : nextSession.status === 'abandoned' ? 'planned' as const : 'started' as const,
    } : item) : currentData.workouts;
    await queue(mutation('upsert_session', `session:${session.id}`, nextSession, current?.version), { ...currentData, sessions, workouts });
    if (nextSession.status !== 'in_progress') await sync();
  }, [queue, sync]);

  const saveExercise = useCallback(async (exercise: Exercise) => {
    if (!data) return;
    const current = data.exercises.find((item) => item.id === exercise.id);
    const nextExercise = { ...exercise, updatedAt: new Date().toISOString() };
    const exercises = current ? data.exercises.map((item) => item.id === exercise.id ? nextExercise : item) : [...data.exercises, nextExercise];
    await queue(mutation('upsert_exercise', `exercise:${exercise.id}`, nextExercise, current?.version), { ...data, exercises });
    await sync();
  }, [data, queue, sync]);

  const saveBodyWeight = useCallback(async (entry: BodyWeightEntry) => {
    if (!data) return;
    const current = data.bodyWeights.find((item) => item.id === entry.id);
    const nextEntry = { ...entry, updatedAt: new Date().toISOString() };
    const bodyWeights = current ? data.bodyWeights.map((item) => item.id === entry.id ? nextEntry : item) : [nextEntry, ...data.bodyWeights];
    await queue(mutation('upsert_body_weight', `weight:${entry.id}`, nextEntry, current?.version), { ...data, bodyWeights });
  }, [data, queue]);

  const removeBodyWeight = useCallback(async (entry: BodyWeightEntry) => {
    if (!data) return;
    await queue(mutation('delete_body_weight', `weight:${entry.id}`, { id: entry.id }, entry.version), { ...data, bodyWeights: data.bodyWeights.filter((item) => item.id !== entry.id) });
  }, [data, queue]);

  const saveSettings = useCallback(async (settings: Settings) => {
    if (!data) return;
    await queue(mutation('update_settings', 'settings', settings), { ...data, settings });
  }, [data, queue]);

  const dismissCoachNoteAction = useCallback(async (note: CoachNote) => {
    if (!data) return;
    await queue(mutation('dismiss_coach_note', `coach-note:${note.id}`, { id: note.id }), {
      ...data, coachNotes: (data.coachNotes ?? []).filter((item) => item.id !== note.id),
    });
  }, [data, queue]);

  const cloneProgramAction = useCallback(async (program: TrainingProgram, startDate: string) => {
    if (!data) return;
    await queue(mutation('clone_program', `program-clone:${crypto.randomUUID()}`, { sourceId: program.id, startDate }), data);
  }, [data, queue]);

  const keepLocalConflict = useCallback(async (conflict: Conflict) => {
    await rebaseMutation(conflict.id, conflict.serverVersion); setConflicts((items) => items.filter((item) => item.id !== conflict.id)); await sync();
  }, [sync]);

  const useServerConflict = useCallback(async (conflict: Conflict) => {
    await removeMutations([conflict.id]); setConflicts((items) => items.filter((item) => item.id !== conflict.id)); await refresh();
  }, [refresh]);

  const replaceData = useCallback(async (next: AppData) => { await persistLocal(next); setData(next); }, [persistLocal]);

  const value = useMemo<ContextValue>(() => ({
    data, loading, authenticated, offlineUnlocked, error, syncState, pendingCount, conflicts,
    login: loginUser, logout: logoutUser, refresh, sync, keepLocalConflict, useServerConflict,
    saveWorkout, removeWorkout, startWorkout, saveSession, saveExercise, saveBodyWeight, removeBodyWeight,
    saveSettings, dismissCoachNote: dismissCoachNoteAction, cloneProgram: cloneProgramAction, replaceData,
  }), [data, loading, authenticated, offlineUnlocked, error, syncState, pendingCount, conflicts, loginUser, logoutUser,
    refresh, sync, keepLocalConflict, useServerConflict, saveWorkout, removeWorkout, startWorkout, saveSession,
    saveExercise, saveBodyWeight, removeBodyWeight, saveSettings, dismissCoachNoteAction, cloneProgramAction, replaceData]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): ContextValue {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp должен использоваться внутри AppProvider');
  return context;
}
